import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useUserStats, useWatchedMovies } from '../hooks/useFirestore'
import { toggleHistoryVisibility, getUserProfile } from '../services/movies'
import Loader from '../components/common/Loader'
import './Profile.css'

function Profile() {
  const { user, logout } = useAuth()
  const { stats, loading: statsLoading } = useUserStats()
  const { movies: recentMovies } = useWatchedMovies()
  const [isPublic, setIsPublic] = useState(false)
  const [copied, setCopied] = useState(false)

  const shareUrl = `${window.location.origin}/shared/${user?.uid}`

  const handleTogglePublic = async () => {
    try {
      await toggleHistoryVisibility(user.uid, !isPublic)
      setIsPublic(!isPublic)
    } catch (err) {
      console.error('Error toggling visibility:', err)
    }
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Error copying link:', err)
    }
  }

  if (statsLoading) {
    return <Loader fullScreen />
  }

  const topGenres = stats?.genreDistribution 
    ? Object.entries(stats.genreDistribution)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
    : []

  return (
    <div className="page profile-page">
      <div className="container">
        <div className="profile-header">
          <div className="profile-avatar-section">
            {user?.photoURL ? (
              <img src={user.photoURL} alt={user.displayName} className="profile-avatar" />
            ) : (
              <div className="profile-avatar profile-avatar-placeholder">
                {user?.displayName?.charAt(0) || '?'}
              </div>
            )}
            <div className="profile-info">
              <h1 className="profile-name">{user?.displayName}</h1>
              <p className="profile-email">{user?.email}</p>
            </div>
          </div>
          <button className="btn btn-secondary" onClick={logout}>
            🚪 Cerrar sesión
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <section className="profile-section">
            <h2 className="section-title">📊 Tus estadísticas</h2>
            <div className="stats-grid">
              <div className="stat-card-large">
                <span className="stat-icon">🎬</span>
                <span className="stat-number">{stats.totalMovies}</span>
                <span className="stat-label">Películas vistas</span>
              </div>
              <div className="stat-card-large">
                <span className="stat-icon">⏱️</span>
                <span className="stat-number">{stats.totalHours}h</span>
                <span className="stat-label">Tiempo total</span>
              </div>
              <div className="stat-card-large">
                <span className="stat-icon">⭐</span>
                <span className="stat-number">{stats.averageRating}</span>
                <span className="stat-label">Puntuación media</span>
              </div>
            </div>
          </section>
        )}

        {/* Top Genres */}
        {topGenres.length > 0 && (
          <section className="profile-section">
            <h2 className="section-title">🎭 Tus géneros favoritos</h2>
            <div className="genres-chart">
              {topGenres.map(([genre, count]) => (
                <div key={genre} className="genre-bar-container">
                  <span className="genre-name">{genre}</span>
                  <div className="genre-bar-wrapper">
                    <div 
                      className="genre-bar" 
                      style={{ width: `${(count / topGenres[0][1]) * 100}%` }}
                    />
                  </div>
                  <span className="genre-count">{count}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Share */}
        <section className="profile-section">
          <h2 className="section-title">🔗 Compartir historial</h2>
          <div className="share-card">
            <p className="share-description">
              Comparte tu historial de películas con amigos. Ellos podrán ver qué películas has visto y tus puntuaciones.
            </p>
            
            <div className="share-toggle">
              <label className="toggle">
                <input 
                  type="checkbox" 
                  checked={isPublic}
                  onChange={handleTogglePublic}
                />
                <span className="toggle-slider"></span>
              </label>
              <span>{isPublic ? 'Historial público' : 'Historial privado'}</span>
            </div>

            {isPublic && (
              <div className="share-link-container">
                <input 
                  type="text" 
                  className="input share-link-input" 
                  value={shareUrl}
                  readOnly
                />
                <button className="btn btn-primary" onClick={handleCopyLink}>
                  {copied ? '✓ Copiado' : 'Copiar'}
                </button>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

export default Profile
